import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, Plus, X } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DemoScheduler } from '@/components/DemoScheduler';
import { useBookDemoEvent } from '@/hooks/useBookDemoEvent';
import HeroVideo from '@/components/HeroVideo';
import Seo from '@/components/Seo';
import { GARU_DEMO_URL } from '@/lib/demoStore';
import merchantRoutes from '@/content/merchantRoutes.json';

const SHOPIFY_APP_STORE_URL =
  'https://apps.shopify.com/garu-virtual-try-on?utm_source=garu_landing&utm_medium=website&utm_campaign=shopify_launch';

/* ─────────────────────────────────────────────────────────────────
   Landing page, 2026-08-29/30 redesign (founder direction, 9 rounds):
   · Aiuta's canvas, measured: Poppins only, titles ~76px weight 400,
     #F4F4F6 page, white 48px-radius cards. No italics, no second face.
   · Three products, each opened by the SAME big title block
     (a CardEyebrow inside each card): Garu Fit (try-on + sizing + swap
     comparison), Garu Plus (every size, fabric), Garu Studio (models +
     product shots). Then merchant controls, setup, CTA.
   · Every render is REAL and floats as a transparent cut-out:
     /studio = demo-store garments on avatars generated from the
     founder's tryon_faces set (staging generate-model → virtual-try-on
     → pose-variation for the backs; rembg for the alpha). /plus = the
     size-edit pipeline's eight renders, cut out the same way.
   · Garu Plus's fabric tiles are macro crops of the demo garments and
     crossfade to the worn render on hover.
   ───────────────────────────────────────────────────────────────── */

type Garment = { src: string; label: string; kind: 'Top' | 'Bottom' | 'Dress' };

/* Shopper experience: the shopper's photo → a size → the try-on with the
   pieces. Size values are illustrative UI (the card says so). */
const SHOPPER_LOOKS: {
  id: string;
  selfie: string;
  worn: string;
  cut: string;
  back: string;
  alt: string;
  items: Garment[];
  size: { value: string; note: string };
  body: string;
}[] = [
  {
    id: 'malibu',
    selfie: '/studio/malibu-selfie.webp',
    worn: '/studio/malibu-look.webp',
    cut: '/studio/malibu-cut.webp',
    back: '/studio/malibu-back-cut.webp',
    alt: 'Real Garu render: poplin blouse and wide-leg trousers, worn',
    items: [
      { src: '/demo/item-poplin-blouse.webp', label: 'Poplin Blouse', kind: 'Top' },
      { src: '/demo/item-wide-leg-trousers.webp', label: 'Wide-Leg Trousers', kind: 'Bottom' },
    ],
    size: { value: 'S', note: 'True to size' },
    body: '5′6″ · 128 lb · waist 27″',
  },
  {
    id: 'selfie2',
    selfie: '/studio/selfie2-selfie.webp',
    worn: '/studio/selfie2-look.webp',
    cut: '/studio/selfie2-cut.webp',
    back: '/studio/selfie2-back-cut.webp',
    alt: 'Real Garu render: square-neck knit top and croc-embossed midi skirt, worn',
    items: [
      { src: '/demo/item-square-neck-top.webp', label: 'Square-Neck Top', kind: 'Top' },
      { src: '/demo/item-croc-skirt.webp', label: 'Croc Midi Skirt', kind: 'Bottom' },
    ],
    size: { value: 'M', note: 'Fits relaxed at the waist' },
    body: '5′7″ · 145 lb · waist 29″',
  },
  {
    id: 'teal',
    selfie: '/studio/teal-selfie.webp',
    worn: '/studio/teal-look.webp',
    cut: '/studio/teal-cut.webp',
    back: '/studio/teal-back-cut.webp',
    alt: 'Real Garu render: off-shoulder ribbed sweater and satin balloon trousers, worn',
    items: [
      { src: '/demo/item-off-shoulder-sweater.webp', label: 'Off-Shoulder Sweater', kind: 'Top' },
      { src: '/demo/item-balloon-trousers.webp', label: 'Balloon Trousers', kind: 'Bottom' },
    ],
    size: { value: 'S', note: 'Snug through the chest' },
    body: '5′4″ · 119 lb · inseam 29″',
  },
  {
    id: 'portrait',
    selfie: '/studio/portrait-selfie.webp',
    worn: '/studio/portrait-look.webp',
    cut: '/studio/portrait-cut.webp',
    back: '/studio/portrait-back-cut.webp',
    alt: 'Real Garu render: draped satin gown, worn',
    items: [{ src: '/demo/item-satin-gown.webp', label: 'Draped Satin Gown', kind: 'Dress' }],
    size: { value: 'XS', note: 'True to size' },
    body: '5′8″ · 121 lb · waist 25″',
  },
];

/* Garu Studio — shots NOT used anywhere above. */
const STUDIO_SHOTS: { src: string; alt: string }[] = [
  { src: '/studio/ponytail-look.webp', alt: 'Oxford shirt and wide-leg trousers' },
  { src: '/studio/curly-look.webp', alt: 'Knitted polo shirt' },
  { src: '/studio/bob-look.webp', alt: 'Polka-dot cowl top and pleated skirt' },
  { src: '/studio/teal-top.webp', alt: 'Off-shoulder ribbed sweater' },
  { src: '/studio/malibu-top.webp', alt: 'Poplin blouse' },
  { src: '/studio/selfie2-top.webp', alt: 'Square-neck knit top' },
];

const PRODUCT_SHOTS: { garment: Garment; worn: string }[] = [
  { garment: { src: '/demo/item-oxford-shirt.webp', label: 'Oxford Shirt', kind: 'Top' }, worn: '/studio/ponytail-top.webp' },
  { garment: { src: '/demo/item-cowl-neck-top.webp', label: 'Polka-Dot Cowl Top', kind: 'Top' }, worn: '/studio/bob-top.webp' },
  { garment: { src: '/demo/item-knitted-polo.webp', label: 'Knitted Polo', kind: 'Top' }, worn: '/studio/curly-look.webp' },
];

/* Reveal-on-scroll: adds `is-in` (see .garu-reveal in index.css) once. */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { threshold: 0, rootMargin: '0px 0px 160px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, className: `garu-reveal ${inView ? 'is-in' : ''}` };
}

/* Auto-cycle an index; a manual pick holds it. */
function useCycle(length: number, ms: number) {
  const [idx, setIdx] = useState(0);
  const [hold, setHold] = useState(false);
  useEffect(() => {
    if (hold) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % length), ms);
    return () => clearInterval(id);
  }, [hold, length, ms]);
  return { idx, pick: (i: number) => { setIdx(i); setHold(true); }, setHold };
}

/* One type scale for the whole page (founder direction 2026-08-30):
   EVERY title is Aiuta's voice — Poppins 400, slight negative tracking,
   sizes 43-60px (their measured range). Body stays Instrument Sans 18/28,
   cards r32 pad 64. The hero alone keeps Afacad: it must stay byte-identical
   to live garu.ai. */
const TITLE_FONT = { fontFamily: "'Poppins', 'Afacad', sans-serif" } as const;
const SUBTITLE = 'text-[15px] md:text-[17px] leading-[1.35] text-neutral-900';
const CARD = 'rounded-[24px] md:rounded-[32px] bg-white';

/* Fixed-width derivatives sit next to every landing image
   (`<name>-260.webp` / `-420.webp`). The widths are fixed rather than
   "half of the original" so the `w` descriptors below are TRUE: a
   descriptor that lies makes a DPR-2 phone pick the 400KB master for a
   130px-wide slot, which is exactly what the first pass did. `src`
   stays the master as the no-srcset fallback. */
const w260 = (src: string) => src.replace('.webp', '-260.webp');
const w420 = (src: string) => src.replace('.webp', '-420.webp');
const w560 = (src: string) => src.replace('.webp', '-560.webp');
/**
 * Every cut-out sits on one shared 667x1600 canvas (bottom-aligned, centred),
 * so `object-contain` scales them all by the same factor. Before this, each
 * figure was cropped tight and normalised to 1600px tall, which let WIDTH bind
 * the contain scale for wide figures — the gown's front (647px across, flared
 * skirt) rendered 12% shorter than its own back. The slot below is sized to the
 * canvas aspect so height binds for every figure.
 */
const FIGURE_SET = (src: string) => `${w260(src)} 260w, ${w420(src)} 420w, ${w560(src)} 560w`;
const FIGURE_SIZES = '(max-width: 640px) 44vw, 200px';

function GroupTitle({ id, name, tagline }: { id: string; name: string; tagline: string }) {
  const r = useReveal<HTMLDivElement>();
  return (
    <div id={id} ref={r.ref} className={`scroll-mt-24 px-6 lg:px-12 pt-16 md:pt-24 pb-5 md:pb-7 ${r.className}`}>
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-4xl md:text-[2.8rem] lg:text-[3.2rem] font-normal tracking-[-0.01em] leading-[1.08] text-neutral-900" style={TITLE_FONT}>{name}</h2>
        <p className={`mt-3 ${SUBTITLE} max-w-[46ch] mx-auto`} style={TITLE_FONT}>{tagline}</p>
      </div>
    </div>
  );
}

function CardEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-400">{children}</p>
  );
}

function SubTitle({ children, center = false }: { children: React.ReactNode; center?: boolean }) {
  return (
    <h3 className={`text-3xl md:text-[2.3rem] lg:text-[2.7rem] font-normal tracking-[-0.01em] leading-[1.12] text-neutral-900 ${center ? 'text-center' : ''}`} style={TITLE_FONT}>
      {children}
    </h3>
  );
}

function Dots({ n, idx, onPick }: { n: number; idx: number; onPick: (i: number) => void }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {Array.from({ length: n }, (_, i) => (
        <button
          key={i}
          type="button"
          tabIndex={-1}
          onClick={() => onPick(i)}
          className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-neutral-900' : 'w-1.5 bg-neutral-300'}`}
        />
      ))}
    </div>
  );
}

/* The size readout, the first version's chip: green dot, label, value + note. */
function SizeChip({ value, note, className = '' }: { value: string; note: string; className?: string }) {
  return (
    <div className={`rounded-2xl bg-white px-4 py-3 shadow-[0_16px_40px_-16px_rgba(20,20,20,0.35)] ring-1 ring-black/5 ${className}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#007A68' }} />
        <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-neutral-400 whitespace-nowrap">Recommended size</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-[26px] font-semibold leading-none tracking-[-0.02em] text-neutral-900">{value}</span>
        <span className="text-[12px] font-medium text-neutral-500">{note}</span>
      </div>
    </div>
  );
}

/* ─── Garu Fit · 1: photo → size → try-on with the pieces ──────────
   Every look is mounted at once and crossfades (a remount left a white
   box on slow connections). The numbered intro plays once, on mount.
   The stage is FLUID: the two figure slots flex to whatever width the
   column leaves them and get their height from the shared 667x1600
   canvas aspect — fixed slot widths (233px each) summed to 890px and
   spilled past the card on every laptop-sized viewport. */
const SLOT = 2; // garment card slots, fixed so the layout never jumps

function Layer({ src, active, className, eager = false, alt = '', sizes = FIGURE_SIZES }: { src: string; active: boolean; className: string; eager?: boolean; alt?: string; sizes?: string }) {
  return (
    <img
      src={src}
      srcSet={FIGURE_SET(src)}
      sizes={sizes}
      alt={active ? alt : ''}
      aria-hidden={!active}
      width={667}
      height={1600}
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : 'low'}
      decoding="async"
      className={`${className} transition-opacity duration-[400ms] ease-out ${active ? 'opacity-100' : 'opacity-0'}`}
    />
  );
}

function ShopperExperience() {
  const c = useCycle(SHOPPER_LOOKS.length, 2600);
  const look = SHOPPER_LOOKS[c.idx];
  const r = useReveal<HTMLDivElement>();
  return (
    <section className="px-6 lg:px-12 py-3">
      <div ref={r.ref} className={`max-w-7xl mx-auto ${r.className}`}>
        <div className={`${CARD} p-6 md:p-12 lg:p-16 overflow-hidden`} onMouseEnter={() => c.setHold(true)} onMouseLeave={() => c.setHold(false)}>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.42fr)_minmax(0,1fr)] items-center gap-6 lg:gap-8">
            <div className="min-w-0">
              <CardEyebrow>Virtual try-on</CardEyebrow>
              <SubTitle>
                Their photo.
                <br />
                Their size.
                <br />
                Your clothes.
              </SubTitle>
              <a href={GARU_DEMO_URL} target="_blank" rel="noopener noreferrer" className="garu-btn-cta mt-6 inline-flex min-h-[44px] items-center justify-center no-underline">
                Try it now
              </a>
              <div className="mt-6 hidden lg:block">
                <Dots n={SHOPPER_LOOKS.length} idx={c.idx} onPick={c.pick} />
              </div>
            </div>

            {/* the stage: one tight cluster — photo · figures · pieces */}
            <div className="relative min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-4 sm:gap-5 md:gap-7">
              <div className="flex gap-3 sm:gap-5 md:gap-7 sm:contents">
                {/* 1 · the size and 2 · the photo */}
                <div className="flex flex-col gap-3 shrink-0 w-[44%] max-w-[190px] sm:w-auto sm:max-w-none sm:contents">
                  <SizeChip value={look.size.value} note={look.size.note} className="sm:hidden w-full" />
                  <div className="garu-seq-photo relative w-full sm:w-[112px] md:w-[132px] shrink-0 -rotate-3">
                    <div className="rounded-[10px] bg-white p-2 pb-7 shadow-[0_24px_50px_-20px_rgba(20,20,20,0.35)] ring-1 ring-black/5">
                      <div className="relative aspect-[3/4] overflow-hidden rounded-[4px] bg-neutral-100">
                        {SHOPPER_LOOKS.map((l, i) => (
                          <Layer key={l.id} src={l.selfie} active={i === c.idx} eager={i === 0} alt="The shopper’s photo" sizes="190px" className="absolute inset-0 w-full h-full object-cover object-top" />
                        ))}
                        <div aria-hidden className="garu-seq-flash absolute inset-0 bg-white pointer-events-none" />
                      </div>
                      <p className="absolute left-0 right-0 bottom-1.5 text-center text-[10px] sm:text-[11px] font-medium text-neutral-500">Photo</p>
                    </div>
                    <span className="absolute -top-3 -right-3 inline-flex items-center gap-1 rounded-full bg-neutral-900 text-white text-[10px] font-medium px-2 py-1 shadow-md">
                      <Check className="w-3 h-3" strokeWidth={3} /> Uploaded
                    </span>
                  </div>
                </div>

                {/* 3 · the model, front and back — clamp widths keep the
                    cluster together instead of stretching to the edges */}
                <div className="relative flex items-end justify-center gap-2 sm:gap-3 md:gap-4 min-w-0 pt-6">
                  <div className="garu-seq-render relative w-[clamp(120px,22vw,186px)] sm:w-[clamp(120px,14vw,186px)] aspect-[667/1600]">
                    {SHOPPER_LOOKS.map((l, i) => (
                      <Layer key={l.id} src={l.cut} active={i === c.idx} eager={i === 0} alt={l.alt} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_30px_40px_rgba(20,20,20,0.18)]" />
                    ))}
                    <SizeChip value={look.size.value} note={look.size.note} className="hidden sm:block absolute bottom-6 right-[72%] w-[150px] md:w-[164px]" />
                  </div>
                  <div className="garu-seq-back relative w-[clamp(120px,22vw,186px)] sm:w-[clamp(120px,14vw,186px)] aspect-[667/1600]">
                    {SHOPPER_LOOKS.map((l, i) => (
                      <Layer key={l.id} src={l.back} active={i === c.idx} alt={`${l.alt}, back view`} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_30px_40px_rgba(20,20,20,0.16)]" />
                    ))}
                  </div>
                </div>
              </div>

              {/* the pieces, tied to the model with short leaders */}
              <div className="garu-seq-items shrink-0 flex flex-row sm:flex-col justify-center gap-3 md:gap-4 self-center max-w-full">
                {Array.from({ length: SLOT }, (_, slot) => {
                  const item = look.items[slot];
                  return (
                    <figure
                      key={slot}
                      className={`relative w-[92px] md:w-[104px] transition-opacity duration-[400ms] ${item ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                      aria-hidden={!item}
                    >
                      <span aria-hidden className="hidden sm:flex absolute right-full top-[45%] w-5 md:w-7 items-center">
                        <span className="w-2 h-2 rounded-full bg-neutral-900 flex-shrink-0" />
                        <span className="flex-1 border-t-2 border-dotted border-neutral-400" />
                      </span>
                      <div className="relative aspect-[4/5] rounded-[14px] overflow-hidden bg-white ring-1 ring-black/5 shadow-[0_16px_40px_-20px_rgba(20,20,20,0.2)]">
                        {SHOPPER_LOOKS.map((l, i) => {
                          const it = l.items[slot];
                          if (!it) return null;
                          return (
                            <img
                              key={l.id}
                              src={it.src}
                              srcSet={`${w260(it.src)} 260w`}
                              sizes="104px"
                              alt={i === c.idx ? `${it.label}, product shot` : ''}
                              aria-hidden={i !== c.idx}
                              width={900}
                              height={1140}
                              loading={i === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[400ms] ease-out ${i === c.idx ? 'opacity-100' : 'opacity-0'}`}
                            />
                          );
                        })}
                      </div>
                      <figcaption className="mt-1.5 text-center text-[10px] sm:text-[11px] text-neutral-500">
                        <span className="font-medium text-neutral-800">{item?.kind ?? ''}</span>
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </div>
            <div className="lg:hidden -mt-2">
              <Dots n={SHOPPER_LOOKS.length} idx={c.idx} onPick={c.pick} />
            </div>
          </div>
          <p className="mt-4 text-[11px] text-neutral-400">Real renders, unretouched. Size readout illustrative.</p>
        </div>
      </div>
    </section>
  );
}

/* ─── Garu Fit · 2: the live tape-measure wizard ───────────────────
   Why it lands where other tools don't: girth is what decides a size.
   Height and weight describe a silhouette, not a fit. ─────────────── */
const SIZING_POINTS: [string, string][] = [
  ['Girth, not guesswork', 'Chest, waist and inseam decide the size — height and weight alone can’t.'],
  ['Your size chart', 'Matched to your own measurements, never a generic one.'],
  ['A fit note every time', 'Not just a letter: where it sits close, where it falls loose.'],
];
const SIZING_COVERS = ['Tops', 'Trousers', 'Dresses', 'Denim', 'Tailoring', 'Swim', 'Footwear', 'Bras'];

function Sizing() {
  const r = useReveal<HTMLDivElement>();
  return (
    <section id="ai-sizing" className="scroll-mt-24 px-6 lg:px-12 py-3">
      <div ref={r.ref} className={`max-w-7xl mx-auto ${CARD} p-6 md:p-12 lg:p-16 ${r.className}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-8 lg:gap-12 items-center">
          <div>
            <CardEyebrow>AI sizing</CardEyebrow>
            <SubTitle>The right size, first time.</SubTitle>
            <p className={`mt-4 ${SUBTITLE} max-w-[40ch]`} style={TITLE_FONT}>
              Most tools ask height and weight, then guess. Garu measures.
            </p>
            <dl className="mt-6 space-y-3.5">
              {SIZING_POINTS.map(([term, detail]) => (
                <div key={term} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: '#007A68' }} strokeWidth={2.5} />
                  <div>
                    <dt className="text-[15px] md:text-base font-semibold text-neutral-900">{term}</dt>
                    <dd className="text-[14px] md:text-[15px] leading-snug text-neutral-500">{detail}</dd>
                  </div>
                </div>
              ))}
            </dl>
            <p className="mt-7 text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-400">Sizes everything you sell</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {SIZING_COVERS.map((cover) => (
                <li key={cover} className="rounded-full bg-[#F4F4F6] px-3 py-1.5 text-[13px] font-medium text-neutral-700">{cover}</li>
              ))}
            </ul>
          </div>
          <div className="mx-auto w-full max-w-[270px] lg:max-w-[290px]">
            <video
              className="w-full rounded-[28px] bg-white border border-neutral-200 shadow-[0_32px_80px_-40px_rgba(20,20,20,0.3)]"
              poster="/widget/sizing-flow.webp"
              width={1380}
              height={2070}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              aria-label="Garu's sizing wizard: height and weight tapes, then chest, waist and inseam"
            >
              {/* phones pull the 213KB cut, not the 780KB one */}
              <source src="/widget/sizing-flow-sm.mp4" type="video/mp4" media="(max-width: 700px)" />
              <source src="/widget/sizing-flow.mp4" type="video/mp4" />
            </video>
            <p className="mt-3 text-center text-xs text-neutral-400">The live widget, captured from a real store.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Garu Fit · 3: side by side with a flat photo swap ──────────────
   The photos ARE the argument. No competitor is named. Laid out as one
   row (question · typical app · Garu) so it fits a laptop viewport. */
function Compare() {
  const r = useReveal<HTMLDivElement>();
  return (
    <section id="compare" className="scroll-mt-24 px-6 lg:px-12 py-3">
      <div ref={r.ref} className={`max-w-7xl mx-auto ${CARD} p-6 md:p-12 lg:p-16 ${r.className}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,0.75fr)_minmax(0,1.35fr)] gap-8 lg:gap-10 items-start">
          <div>
            <CardEyebrow>Side by side</CardEyebrow>
            <SubTitle>Most try-ons are a flat photo swap.</SubTitle>
            <p className="mt-4 text-[15px] md:text-base leading-relaxed text-neutral-500 max-w-[40ch]">Same shopper, same trousers. The typical app pastes the product over her photo. Garu renders her wearing it.</p>
            <div className="mt-6 flex items-center gap-3">
              <figure className="w-20">
                <img src="/compare/shopper-photo-256.webp" alt="The shopper's own photo" width={256} height={341} loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover object-top rounded-xl ring-1 ring-neutral-200/70" />
                <figcaption className="mt-1.5 text-center text-[11px] font-medium text-neutral-500">Her photo</figcaption>
              </figure>
              <Plus className="w-4 h-4 text-neutral-400 flex-shrink-0 mb-5" aria-hidden />
              <figure className="w-20">
                <img src="/compare/pants-256.webp" alt="The product: grey tailored trousers" width={256} height={384} loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover object-top rounded-xl ring-1 ring-neutral-200/70" />
                <figcaption className="mt-1.5 text-center text-[11px] font-medium text-neutral-500">The trousers</figcaption>
              </figure>
              <ArrowRight className="w-4 h-4 text-neutral-400 flex-shrink-0 mb-5" aria-hidden />
              <p className="text-[13px] font-medium text-neutral-500 max-w-[12ch] mb-5">What does she get back?</p>
            </div>
          </div>
          <figure className="min-w-0">
            <p className="mb-3 text-[14px] font-semibold text-neutral-500">Typical try-on apps</p>
            <div className="relative max-w-[210px]">
              <img src="/compare/swap-1-480.webp" alt="Flat swap result: the trousers pasted into the selfie" width={480} height={565} loading="lazy" decoding="async" className="block w-full aspect-[2/3] object-cover object-top rounded-2xl grayscale-[0.45] opacity-75" />
              <span className="absolute top-2.5 left-2.5 rounded-full bg-black/55 backdrop-blur-sm px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-white/90">2D swap</span>
            </div>
            <figcaption className="mt-4">
              <ul className="space-y-2">
                {['A flat picture, pasted over her photo', 'No sizing, she still guesses', 'Fabric, rise and drape drift'].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-neutral-400" aria-hidden />
                    <span className="text-[13px] leading-snug text-neutral-500">{line}</span>
                  </li>
                ))}
              </ul>
            </figcaption>
          </figure>
          <figure className="min-w-0">
            <p className="mb-3 text-[14px] font-semibold text-neutral-900">Garu</p>
            <div className="relative grid grid-cols-2 gap-3 max-w-[440px]">
              <img src="/compare/garu-render-620.webp" alt="Garu's render: the same shopper standing in the trousers, front view" width={620} height={934} loading="lazy" decoding="async" className="block w-full aspect-[2/3] object-cover rounded-2xl" />
              <img src="/compare/garu-render-back-620.webp" alt="Garu's render of the same look from behind" width={620} height={911} loading="lazy" decoding="async" className="block w-full aspect-[2/3] object-cover rounded-2xl" />
              <span className="absolute top-3 left-3 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-white" style={{ backgroundColor: '#007A68' }}>Garu</span>
              <SizeChip value="US 6" note="True to size" className="absolute left-3 bottom-3" />
            </div>
            <figcaption className="mt-4">
              <ul className="space-y-2">
                {['A photoreal render of her wearing it, front and back', 'A size recommendation with every look', 'Garment accuracy, fabric, rise and drape preserved', 'A body built from her height, weight and shape'].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#007A68' }} aria-hidden />
                    <span className="text-[13px] leading-snug text-neutral-700">{line}</span>
                  </li>
                ))}
              </ul>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

/* ─── Garu Plus: every size, and the fabric that survives it ──────── */
const SIZE_MORPH_SIZES = ['S', 'M', 'L', 'XL'] as const;
type MorphSize = (typeof SIZE_MORPH_SIZES)[number];
const SIZE_MORPH_NOTES: Record<MorphSize, string> = {
  S: 'Snug through the chest',
  M: 'Recommended · true to size',
  L: 'Relaxed through the body',
  XL: 'Loose, longer at the hem',
};
const FABRIC_TILES = [
  { src: '/fabric/croc-skirt.webp', worn: '/studio/selfie2-look.webp', label: 'Garment accuracy', note: 'Seams, grain and hardware, kept', alt: 'Croc-embossed leather, up close' },
  { src: '/fabric/off-shoulder-sweater.webp', worn: '/studio/teal-top.webp', label: 'Fabric', note: 'Knit, satin, leather read as themselves', alt: 'Ribbed knit, up close' },
  { src: '/fabric/balloon-trousers.webp', worn: '/studio/teal-look.webp', label: 'Drape', note: 'How it falls on this body, in this size', alt: 'Washed satin, up close' },
];
const plusSrc = (size: MorphSize, view: 'front' | 'back') => `/plus/${size.toLowerCase()}-${view}-cut.webp`;

function SizeMorph({ onStartTrial }: { onStartTrial: () => void }) {
  const [size, setSize] = useState<MorphSize>('M');
  const [autoPlay, setAutoPlay] = useState(true);
  const r = useReveal<HTMLDivElement>();
  useEffect(() => {
    if (!autoPlay) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => {
      setSize((s) => SIZE_MORPH_SIZES[(SIZE_MORPH_SIZES.indexOf(s) + 1) % SIZE_MORPH_SIZES.length]);
    }, 2600);
    return () => clearInterval(id);
  }, [autoPlay]);

  /* Three grid children rather than two, so the phone can put the figures
     between the heading and the controls (heading → avatar → sizes → fabric)
     while the desktop keeps copy-left / figures-right: on lg the heading and
     the controls both sit in column 1 (rows 1 and 2) and the figures span
     both rows of column 2. */
  return (
    <section className="px-6 lg:px-12 py-3">
      <div ref={r.ref} className={`max-w-7xl mx-auto ${CARD} p-6 md:p-12 lg:p-16 ${r.className}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:grid-rows-[auto_auto] items-center gap-6 lg:gap-x-10 lg:gap-y-0">
          {/* 1 · the pitch */}
          <div className="lg:col-start-1 lg:row-start-1 lg:self-end">
            <SubTitle>See how every size fits on them.</SubTitle>
            <p className={`mt-4 ${SUBTITLE} max-w-[34ch]`} style={TITLE_FONT}>Garment accuracy, fabric and drape, preserved at every size.</p>
          </div>

          {/* 2 · the avatar, with the size selector centred beneath it */}
          <div className="min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2">
            <div className="flex items-end justify-center gap-6 md:gap-10 min-w-0">
            {(['front', 'back'] as const).map((view) => (
              <div key={view} className="relative flex-1 min-w-0 max-w-[200px] aspect-[667/1600]">
                {SIZE_MORPH_SIZES.map((sz) => (
                  <img
                    key={sz}
                    src={plusSrc(sz, view)}
                    srcSet={FIGURE_SET(plusSrc(sz, view))}
                    sizes={FIGURE_SIZES}
                    alt={sz === size ? `Size ${sz}, ${view} view` : ''}
                    aria-hidden={sz !== size}
                    width={667}
                    height={1600}
                    loading="lazy"
                    decoding="async"
                    className={`absolute inset-0 w-full h-full object-contain drop-shadow-[0_30px_40px_rgba(20,20,20,0.16)] transition-opacity duration-500 ${
                      sz === size ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-medium text-neutral-500 capitalize">{view}</span>
              </div>
            ))}
            </div>
            {/* the size selector sits centred under the figures it changes */}
            <div className="mt-10 flex flex-col items-center">
              <div className="flex items-center gap-2" role="radiogroup" aria-label="Size">
              {SIZE_MORPH_SIZES.map((sz) => (
                <button
                  key={sz}
                  type="button"
                  role="radio"
                  aria-checked={size === sz}
                  onClick={() => {
                    setSize(sz);
                    setAutoPlay(false);
                  }}
                  className={`h-11 min-w-[44px] px-4 rounded-full text-sm font-semibold transition ${
                    size === sz ? 'bg-neutral-900 text-white' : 'bg-[#F4F4F6] text-neutral-700 hover:bg-neutral-200'
                  }`}
                  >
                    {sz}
                  </button>
                ))}
              </div>
              <p key={size} className="garu-fade-in mt-3 text-[15px] text-neutral-500">{SIZE_MORPH_NOTES[size]}</p>
            </div>
          </div>

          {/* 3 · the CTA, then the fabric */}
          <div className="mt-6 lg:mt-0 lg:col-start-1 lg:row-start-2 lg:self-start">
            <button type="button" onClick={onStartTrial} className="garu-btn-cta">
              Start free trial
            </button>
            {/* what survives the re-render: the fabric up close; hover → on the body */}
            <div className="mt-8 flex gap-3 md:gap-4">
              {FABRIC_TILES.map((t) => (
                <figure key={t.label} className="group flex-1 min-w-0 max-w-[104px]">
                  <div className="relative aspect-square overflow-hidden rounded-2xl bg-[#F4F4F6]">
                    <img src={t.src} srcSet={`${w260(t.src)} 260w`} sizes="104px" alt={t.alt} width={520} height={520} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                    <img src={w260(t.worn)} alt="" aria-hidden width={260} height={334} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover object-[50%_20%] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  </div>
                  <figcaption className="mt-2 text-[11px] md:text-[12px] font-medium leading-tight text-neutral-900">
                    {t.label}
                    <span className="block font-normal text-neutral-400 text-[10px] md:text-[11px] leading-tight">{t.note}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Garu Studio: AI fashion models + product shots (compact) ────── */
function Studio({ onStartTrial }: { onStartTrial: () => void }) {
  const r = useReveal<HTMLDivElement>();
  const cards = [
    ['Cut production costs', 'Your brand standards at a fraction of the overhead.'],
    ['Days, not weeks', 'Send the garment photo, get the campaign back.'],
    ['Every image checked', 'A person reviews every shot before it reaches you.'],
    ['Full customization', 'Your models, backgrounds, lighting, art direction.'],
  ];
  return (
    <section className="px-6 lg:px-12 py-3">
      <div ref={r.ref} className={`max-w-7xl mx-auto ${CARD} p-6 md:p-12 lg:p-16 ${r.className}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-8 lg:gap-14 items-center">
          <div>
            <SubTitle>Models that reflect your actual customers.</SubTitle>
            <p className={`mt-4 ${SUBTITLE}`} style={TITLE_FONT}>No casting. No reshoots.</p>
            <button type="button" onClick={onStartTrial} className="garu-btn-cta mt-7">
              Start free trial
            </button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
            {STUDIO_SHOTS.map((s) => (
              <img key={s.src} src={w260(s.src)} alt={s.alt} width={260} height={334} loading="lazy" decoding="async" className="w-full aspect-[3/4] object-cover object-top rounded-2xl bg-white ring-1 ring-neutral-200/60" />
            ))}
          </div>
        </div>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map(([t, b]) => (
            <div key={t} className="rounded-2xl bg-[#F4F4F6] p-5">
              <p className="text-[15px] font-semibold text-neutral-900">{t}</p>
              <p className="mt-1 text-[13px] leading-relaxed text-neutral-600">{b}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-6 md:gap-10 items-center">
          <p className="text-base md:text-lg text-neutral-900">
            Send a flat-lay. <span className="text-neutral-500">Get the campaign.</span>
          </p>
          <div className="grid grid-cols-3 gap-3">
            {PRODUCT_SHOTS.map((p) => (
              <figure key={p.garment.src} className="flex items-center gap-2">
                <img src={w260(p.garment.src)} alt={p.garment.label} width={260} height={329} loading="lazy" decoding="async" className="w-[38%] aspect-square object-cover rounded-xl bg-[#F4F4F6]" />
                <ArrowRight className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" strokeWidth={2} aria-hidden />
                <img src={w260(p.worn)} alt={`${p.garment.label}, on a model`} width={260} height={334} loading="lazy" decoding="async" className="flex-1 min-w-0 aspect-[3/4] object-cover object-top rounded-xl bg-white ring-1 ring-neutral-200/60" />
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Merchant controls: dashboard + what moves ───────────────────── */
function Controls() {
  const r = useReveal<HTMLDivElement>();
  return (
    <section id="controls" className="scroll-mt-24 px-6 lg:px-12 pt-16 md:pt-[11.25rem] pb-3">
      <div ref={r.ref} className={`max-w-7xl mx-auto ${CARD} p-6 md:p-12 lg:p-16 ${r.className}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] gap-8 lg:gap-12 items-center">
          <div className="min-w-0">
            <CardEyebrow>Merchant controls</CardEyebrow>
            <SubTitle>
              You stay
              <br />
              in control.
            </SubTitle>
            <ul className="mt-5 space-y-2 text-[14px] md:text-[15px] text-neutral-800">
              {['Enable try-on per product or collection', 'Button label, size, corners and colours, previewed live', 'The full funnel per product: sessions, try-ons, add-to-carts'].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#007A68' }} strokeWidth={2.5} />
                  {t}
                </li>
              ))}
            </ul>
            {/* what moves: conversion up, size-related returns down */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ImpactTile
                title="Conversion"
                headline="Up"
                caption="Shoppers who try on add to cart more often."
                bars={[
                  { label: 'Browsed', value: 0.42, tone: 'muted' },
                  { label: 'Tried on', value: 1, tone: 'accent' },
                ]}
                unit="add-to-cart rate, indexed"
              />
              <ImpactTile
                title="Returns"
                headline="Down"
                caption="42% of fashion returns are size-related."
                bars={[
                  { label: 'Size-related', value: 0.42, tone: 'accent' },
                  { label: 'Everything else', value: 0.58, tone: 'muted' },
                ]}
                unit="share of returns, fashion"
              />
            </div>
            <p className="mt-2 text-[11px] text-neutral-400">Bars illustrative; your own numbers live in the dashboard.</p>
          </div>
          <figure className="mx-auto w-full max-w-[480px] min-w-0">
            <div className="relative pb-14 md:pb-16">
              <div className="garu-float-slow rounded-2xl overflow-hidden bg-white w-[86%] shadow-[0_24px_60px_-28px_rgba(20,20,20,0.14),0_8px_20px_-12px_rgba(20,20,20,0.06)] ring-1 ring-neutral-200/60">
                <img src="/widget/dashboard-analytics-1280.webp" alt="Garu analytics dashboard: generations, add-to-carts, size recommendations, and the session-to-cart funnel" width={1280} height={970} loading="lazy" decoding="async" className="w-full h-auto block" />
              </div>
              <div className="garu-float-slower absolute bottom-0 right-0 w-[60%] rounded-2xl overflow-hidden bg-white shadow-[0_32px_72px_-28px_rgba(20,20,20,0.28)] ring-1 ring-neutral-200/70">
                <img src="/widget/dashboard-branding-1280.webp" alt="Garu branding settings: button label, size, corners and colours with a live product-page preview" width={1280} height={932} loading="lazy" decoding="async" className="w-full h-auto block" />
              </div>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}

/* A stat tile with two thin horizontal bars — one hue, direct labels,
   muted baseline; no axis, no legend (labels carry identity). */
function ImpactTile({ title, headline, caption, bars, unit }: { title: string; headline: string; caption: string; bars: { label: string; value: number; tone: 'accent' | 'muted' }[]; unit: string }) {
  return (
    <div className="rounded-2xl bg-[#F4F4F6] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[12px] font-semibold text-neutral-500">{title}</p>
        <p className="text-xl font-normal tracking-[-0.02em] text-neutral-900 inline-flex items-center gap-1">
          {headline}
          <ArrowRight className={`w-4 h-4 ${headline === 'Up' ? '-rotate-90' : 'rotate-90'}`} strokeWidth={2} style={{ color: '#007A68' }} aria-hidden />
        </p>
      </div>
      <div className="mt-3 space-y-2" role="img" aria-label={`${title}: ${bars.map((b) => `${b.label} ${Math.round(b.value * 100)}`).join(', ')} (${unit})`}>
        {bars.map((b) => (
          <div key={b.label} className="grid grid-cols-[88px_1fr_auto] items-center gap-2 text-[12px]">
            <span className="text-neutral-600 truncate">{b.label}</span>
            <span className="h-1.5 rounded-full bg-white overflow-hidden">
              <span className="block h-full rounded-full" style={{ width: `${b.value * 100}%`, backgroundColor: b.tone === 'accent' ? '#007A68' : '#C9CCD1' }} />
            </span>
            <span className="tabular-nums text-neutral-900 font-medium w-9 text-right">{Math.round(b.value * 100)}%</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[12px] leading-snug text-neutral-600">{caption}</p>
    </div>
  );
}

/* ─── Setup + pricing, one strip ────────────────────────────────── */
function Setup() {
  const r = useReveal<HTMLDivElement>();
  const steps = ['Install from the Shopify App Store', 'Pick the products to enable', 'The Try-on button is live'];
  return (
    <section id="setup" className="scroll-mt-24 px-6 lg:px-12 py-3">
      <div ref={r.ref} className={`max-w-7xl mx-auto ${CARD} p-6 md:p-12 lg:p-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 ${r.className}`}>
        <div>
          <CardEyebrow>Setup</CardEyebrow>
          <SubTitle>Live on your store in minutes.</SubTitle>
          <ol className="mt-7 space-y-4">
            {steps.map((s, i) => (
              <li key={s} className="flex items-center gap-4 text-[17px] text-neutral-800">
                <span className="w-8 h-8 rounded-full bg-neutral-900 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { name: 'Starter', price: '$199', per: '/mo', body: '800 try-ons a month. Sizing, try-on, analytics.' },
            { name: 'Growth', price: '$399', per: '/mo', body: 'More volume, Garu Plus size morph, priority support.' },
          ].map((p) => (
            <div key={p.name} className="rounded-3xl bg-[#F4F4F6] p-6 md:p-7 flex flex-col">
              <p className="text-sm font-semibold text-neutral-500">{p.name}</p>
              <p className="mt-2 text-4xl font-semibold text-neutral-900 tracking-[-0.02em]">
                {p.price}
                <span className="text-base font-medium text-neutral-500">{p.per}</span>
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-neutral-600 flex-1">{p.body}</p>
              <Link to="/pricing" className="mt-5 text-sm font-medium text-neutral-900 inline-flex items-center gap-1.5">
                See pricing <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
          ))}
          <p className="sm:col-span-2 text-sm text-neutral-500">Every self-serve plan starts with a 14-day free trial. Billing starts on day 15.</p>
        </div>
      </div>
    </section>
  );
}

/* ─── Closing CTA + the Explore strip (SEO internal paths) ──────── */
const EXPLORE_NAV = merchantRoutes.nav as {
  solutions: { href: string; label: string }[];
  compare: { href: string; label: string }[];
  // The three field guides. Before this group the returns article had
  // exactly one incoming internal link (/blog) for a non-JS crawler.
  guides: { href: string; label: string }[];
};

function Closing({ onStartTrial }: { onStartTrial: () => void }) {
  return (
    <>
      <section className="px-6 lg:px-12 pt-16 md:pt-[11.25rem] pb-8 md:pb-12">
        <div
          className="max-w-7xl mx-auto rounded-[40px] md:rounded-[48px] px-8 py-16 md:py-24 text-center"
          style={{
            backgroundColor: '#0E0E0E',
            backgroundImage: 'radial-gradient(ellipse 70% 55% at 50% 0%, rgba(0, 122, 104, 0.16) 0%, transparent 65%)',
          }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-[3.75rem] font-normal tracking-[-0.01em] leading-[1.08] text-white" style={TITLE_FONT}>Bring the fitting room to your store.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={SHOPIFY_APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="garu-btn-cta-on-dark">
              Install on Shopify
            </a>
            <button type="button" onClick={onStartTrial} className="garu-btn-ghost-on-dark">
              Start free trial
            </button>
          </div>
        </div>
      </section>
      <nav aria-label="Explore" className="px-6 lg:px-12 pb-12">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-x-5 gap-y-2 text-xs text-neutral-500">
          {[...EXPLORE_NAV.solutions, ...EXPLORE_NAV.compare, ...EXPLORE_NAV.guides].map((l) => (
            <Link key={l.href} to={l.href} className="hover:text-neutral-900">
              {l.label}
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}

/* ─── Provenance strip: Garu has no merchant logos yet, so the band
   under the fold carries the team's background. Swap in client logos
   here as they land. ─────────────────────────────────────────────── */
function TeamStrip() {
  return (
    <section className="px-6 lg:px-12 py-5 md:py-7 bg-white border-b border-neutral-200/70">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10 opacity-80">
        <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-neutral-400">Built by a team from</p>
        <div className="flex items-center gap-8 md:gap-10 flex-wrap justify-center">
          <img src="/images/brands/meta.svg" alt="Meta" className="h-3.5 md:h-4 w-auto select-none" loading="lazy" decoding="async" draggable={false} />
          <svg className="h-4 md:h-[18px] w-auto" viewBox="0 0 74 24" xmlns="http://www.w3.org/2000/svg" aria-label="Google" role="img">
            <path d="M9.24 8.19v2.46h5.88c-.18 1.38-.64 2.39-1.34 3.1-.86.86-2.2 1.8-4.54 1.8-3.62 0-6.45-2.92-6.45-6.54s2.83-6.54 6.45-6.54c1.95 0 3.38.77 4.43 1.76L15.4 2.5C13.94 1.08 11.98 0 9.24 0 4.28 0 .11 4.04.11 9s4.17 9 9.13 9c2.68 0 4.7-.88 6.28-2.52 1.62-1.62 2.13-3.91 2.13-5.75 0-.57-.04-1.1-.13-1.54H9.24z" fill="#4285F4" />
            <path d="M25 6.19c-3.21 0-5.83 2.44-5.83 5.81 0 3.34 2.62 5.81 5.83 5.81s5.83-2.46 5.83-5.81c0-3.37-2.62-5.81-5.83-5.81zm0 9.33c-1.76 0-3.28-1.45-3.28-3.52 0-2.09 1.52-3.52 3.28-3.52s3.28 1.43 3.28 3.52c0 2.07-1.52 3.52-3.28 3.52z" fill="#EA4335" />
            <path d="M53.58 7.49h-.09c-.57-.68-1.67-1.3-3.06-1.3C47.53 6.19 45 8.72 45 12c0 3.26 2.53 5.81 5.43 5.81 1.39 0 2.49-.62 3.06-1.32h.09v.81c0 2.22-1.19 3.41-3.1 3.41-1.56 0-2.53-1.12-2.93-2.07l-2.22.92c.64 1.54 2.33 3.43 5.15 3.43 2.99 0 5.52-1.76 5.52-6.05V6.49h-2.42v1zm-2.93 8.03c-1.76 0-3.1-1.5-3.1-3.52 0-2.05 1.34-3.52 3.1-3.52 1.74 0 3.1 1.5 3.1 3.54 0 2.02-1.37 3.5-3.1 3.5z" fill="#4285F4" />
            <path d="M38 6.19c-3.21 0-5.83 2.44-5.83 5.81 0 3.34 2.62 5.81 5.83 5.81s5.83-2.46 5.83-5.81c0-3.37-2.62-5.81-5.83-5.81zm0 9.33c-1.76 0-3.28-1.45-3.28-3.52 0-2.09 1.52-3.52 3.28-3.52s3.28 1.43 3.28 3.52c0 2.07-1.52 3.52-3.28 3.52z" fill="#FBBC05" />
            <path d="M58 .24h2.51v17.57H58z" fill="#34A853" />
            <path d="M68.26 15.52c-1.3 0-2.22-.59-2.82-1.76l7.77-3.21-.26-.66c-.48-1.3-1.96-3.7-4.97-3.7-2.99 0-5.48 2.35-5.48 5.81 0 3.26 2.46 5.81 5.76 5.81 2.66 0 4.2-1.63 4.84-2.57l-1.98-1.32c-.66.96-1.56 1.6-2.86 1.6zm-.18-7.15c1.03 0 1.91.53 2.2 1.28l-5.25 2.17c0-2.44 1.73-3.45 3.05-3.45z" fill="#EA4335" />
          </svg>
          <div className="flex items-center gap-1.5">
            <svg className="h-3.5 md:h-4 w-auto" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <rect width="10" height="10" fill="#F25022" /><rect x="11" width="10" height="10" fill="#7FBA00" /><rect y="11" width="10" height="10" fill="#00A4EF" /><rect x="11" y="11" width="10" height="10" fill="#FFB900" />
            </svg>
            <span className="text-sm md:text-base font-semibold text-[#737373] tracking-[-0.01em]">Microsoft</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Route-specific intro (keyword landing pages only; "/" has none) ─ */
type RouteIntroContent = {
  eyebrow: string;
  heading: string;
  headingEmphasis: string;
  paragraphs: string[];
  points: { term: string; detail: string }[];
};

function RouteIntro({ content }: { content: RouteIntroContent }) {
  const r = useReveal<HTMLDivElement>();
  return (
    <section id="overview" className="scroll-mt-24 px-6 lg:px-12 pt-16 md:pt-24">
      <div ref={r.ref} className={`max-w-7xl mx-auto rounded-[40px] md:rounded-[48px] bg-white p-8 md:p-12 lg:p-16 ${r.className}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-400 mb-4">{content.eyebrow}</p>
            <SubTitle>
              {content.heading} {content.headingEmphasis}
            </SubTitle>
          </div>
          <div>
            {content.paragraphs.map((p) => (
              <p key={p.slice(0, 40)} className="text-base md:text-[17px] leading-relaxed text-neutral-600 mb-5 last:mb-0">
                {p}
              </p>
            ))}
            <dl className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {content.points.map((pt) => (
                <div key={pt.term}>
                  <dt className="flex items-center gap-2 text-[14px] font-semibold text-neutral-900">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#007A68' }} />
                    {pt.term}
                  </dt>
                  <dd className="mt-1.5 ml-3.5 text-[14px] leading-relaxed text-neutral-500">{pt.detail}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}

const ROUTE_CONTENT = merchantRoutes.routes as Record<
  string,
  {
    title: string;
    description: string;
    hero: { prefix: string; emphasis: string; sub: string };
    intro?: RouteIntroContent;
  }
>;

export default function MerchantsSimple() {
  const location = useLocation();
  const [showDemoModal, setShowDemoModal] = useState(false);
  // The footer's shared "Book a demo" link fires a window event; the page's
  // own CTAs are trial signups. Same form, same backend, different framing.
  const [modalIntent, setModalIntent] = useState<'demo' | 'trial'>('demo');
  useBookDemoEvent(() => {
    setModalIntent('demo');
    setShowDemoModal(true);
  });
  const openTrial = () => {
    setModalIntent('trial');
    setShowDemoModal(true);
  };

  const wordmarkHomeRef = useRef<HTMLDivElement | null>(null);
  const morphRef = useRef<HTMLDivElement | null>(null);
  const morphTextRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const morph = morphRef.current;
    const home = wordmarkHomeRef.current;
    if (!morph || !home) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const DOCK_DISTANCE = 320;
    let homeTopDoc = 0;
    let homeLeft = 0;
    let homeH = 1;
    const measure = () => {
      const r = home.getBoundingClientRect();
      homeTopDoc = r.top + window.scrollY;
      homeLeft = r.left;
      homeH = r.height || 1;
    };
    const tick = () => {
      const y = window.scrollY;
      const restTop = homeTopDoc - y;
      const desktop = window.innerWidth >= 1024;
      if (!desktop || reduced) {
        morph.style.transform = `translate(${homeLeft}px, ${restTop}px)`;
        morph.style.opacity = y > 24 ? '0' : '1';
        if (morphTextRef.current) morphTextRef.current.style.color = '#FFFFFF';
        return;
      }
      const target = document.querySelector('[data-garu-logo]')?.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, y / DOCK_DISTANCE));
      const targetScale = target ? target.height / homeH : 0.02;
      const scale = 1 + (targetScale - 1) * p;
      const tx = homeLeft + ((target?.left ?? 0) - homeLeft) * p;
      const ty = restTop + ((target?.top ?? 0) - restTop) * p;
      morph.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
      morph.style.opacity = p >= 0.93 ? '0' : '1';
      if (morphTextRef.current) {
        morphTextRef.current.style.color = p > 0.55 ? '#141414' : '#FFFFFF';
      }
    };
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    const onResize = () => {
      measure();
      tick();
    };
    // Fonts shifting metrics after load would leave a stale home rect.
    measure();
    tick();
    const t = setTimeout(onResize, 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const routePath = location.pathname.replace(/\/+$/, '') || '/';
  const route = ROUTE_CONTENT[routePath] ?? ROUTE_CONTENT['/merchants'];
  const seo = {
    title: route.title,
    description: route.description,
    canonicalPath: ROUTE_CONTENT[routePath] ? routePath : '/merchants',
  };
  const heroHeading = route.hero;

  // Hash / sizing-route deep links.
  useEffect(() => {
    const id = location.hash
      ? location.hash.replace('#', '')
      : ['/merchants/ai-sizing', '/shopify-ai-sizing', '/shopify-size-finder'].includes(routePath)
        ? 'ai-sizing'
        : '';
    if (!id) return;
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempt < 8) requestAnimationFrame(() => tryScroll(attempt + 1));
    };
    tryScroll();
  }, [routePath, location.hash]);

  // FAQPage schema is published from /faq (apps/web/src/pages/Faq.tsx).
  // The landing page only carries one schema block so we don't fragment
  // the FAQ rich result across multiple URLs.
  //
  // Mirrors merchantServiceSchema() in scripts/generate-route-html.js —
  // keep the two in step; <Seo> replaces the snapshot's block with this
  // one on hydration.
  //
  // Service, not SoftwareApplication: Google's Software App rich result
  // requires aggregateRating or review, and the Shopify listing has no
  // reviews yet, so every merchant page failed rich-result validation
  // with no rich result possible in return. Switch back — with a REAL
  // rating read from the listing — once the app has reviews.
  //
  // `url` tracks the current route rather than always claiming
  // /merchants — pointing nine URLs at one `url` was another signal
  // telling Google these pages were the same entity.
  const canonicalUrl = `https://www.garu.ai${seo.canonicalPath === '/' ? '/' : seo.canonicalPath}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Garu for Shopify',
    serviceType: 'AI sizing and virtual try-on for Shopify stores',
    url: canonicalUrl,
    description: route.description,
    provider: { '@id': 'https://www.garu.ai/#organization' },
    audience: { '@type': 'BusinessAudience', audienceType: 'Shopify fashion merchants' },
    sameAs: SHOPIFY_APP_STORE_URL,
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '199',
      highPrice: '399',
      priceCurrency: 'USD',
      url: 'https://www.garu.ai/pricing',
    },
  };

  return (
    <div className="theme-luxe-landing min-h-screen bg-white text-foreground flex flex-col overflow-x-hidden">
      <Seo title={seo.title} description={seo.description} canonicalPath={seo.canonicalPath} robots="index,follow" jsonLd={jsonLd} />

      {/* Ghost nav over the full-bleed hero film; solidifies to the
          standard white bar on scroll. */}
      <Header overlay />

      {/* Morphing wordmark : the visible giant "garu". Fixed so it
          can glide from its hero home into the navbar logo slot,
          positioned every frame by the scroll effect above. */}
      <div
        ref={morphRef}
        aria-hidden
        className="fixed top-0 left-0 z-[70] select-none pointer-events-none will-change-transform transition-opacity duration-500"
        style={{ transformOrigin: 'top left' }}
      >
        <span
          ref={morphTextRef}
          className="block font-bold leading-none whitespace-nowrap"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 'clamp(7rem, 25vw, 27rem)',
            letterSpacing: '0.01em',
            color: '#FFFFFF',
            textShadow: '0 4px 60px rgba(0,0,0,0.2)',
            transition: 'color 250ms ease',
          }}
        >
          garu
        </span>
      </div>

      <main id="main-content" className="relative z-0 bg-[#F4F4F6]">
        {/* Full-bleed cinematic hero — the editorial film, the giant
            wordmark sunk into the fold, the one-line pitch bottom-left.
            (hero-editorial.mp4 = tryon3 re-encoded with the camera
            overlays cropped out; never use raw tryon3.) 100svh so mobile
            browser chrome doesn't push the copy below the fold. */}
        <section className="relative w-full h-[100svh] min-h-[640px] overflow-hidden">
          <HeroVideo src="/videos/hero-editorial.mp4" srcMobile="/videos/hero-editorial-sm.mp4" poster="/posters/hero-editorial.webp" className="absolute inset-0 w-full h-full object-cover" objectPosition="center center" />
          <div
            aria-hidden
            className="md:hidden absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.12) 20%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.34) 58%, rgba(0,0,0,0.78) 100%)' }}
          />
          <div
            aria-hidden
            className="hidden md:block absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.10) 22%, rgba(0,0,0,0) 42%, rgba(0,0,0,0.16) 62%, rgba(0,0,0,0.62) 100%)' }}
          />
          <div className="absolute inset-x-0 bottom-0 px-4 lg:px-10 pb-0">
            <div className="max-w-[1840px] mx-auto flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="order-1 max-w-[480px] px-2 lg:px-0 pb-6 lg:pb-[9svh] lg:text-left">
                <h1 className="font-heading text-[1.9rem] md:text-[2.2rem] lg:text-[2.5rem] font-semibold tracking-[-0.02em] leading-[1.08] text-white mb-4">
                  <span>{heroHeading.prefix}</span>{' '}
                  {heroHeading.emphasis && (
                    <span className="font-normal">{heroHeading.emphasis}</span>
                  )}
                </h1>
                <p className="text-[15px] md:text-base leading-relaxed text-white/85 mb-6 max-w-[46ch]">{heroHeading.sub}</p>
                <div aria-hidden className="flex items-center gap-2 text-white/70 select-none pointer-events-none">
                  <span className="text-[11px] font-medium tracking-[0.18em] uppercase">Scroll</span>
                  <svg className="w-3.5 h-3.5 animate-bounce" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14" />
                    <path d="m19 12-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {/* The wordmark HOME: an invisible placeholder that reserves the
                  resting geometry; the visible mark is the fixed morph element
                  above, which flies into the navbar logo slot on scroll. */}
              <div ref={wordmarkHomeRef} aria-hidden className="order-2 select-none pointer-events-none -mx-2 lg:mx-0 pb-5 lg:pb-12">
                <span className="block font-bold leading-none whitespace-nowrap opacity-0" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(7rem, 25vw, 27rem)', letterSpacing: '0.01em' }}>
                  garu
                </span>
              </div>
            </div>
          </div>
        </section>

        <TeamStrip />
        {route.intro ? <RouteIntro content={route.intro} /> : null}

        <GroupTitle id="fit" name="Garu Fit" tagline="Virtual try-on and AI sizing on your product page." />
        <ShopperExperience />
        <Sizing />
        <Compare />

        <GroupTitle id="plus" name="Garu Plus" tagline="Every size on their body, fabric and drape intact." />
        <SizeMorph onStartTrial={openTrial} />

        <GroupTitle id="studio" name="Garu Studio" tagline="AI fashion models and product shots from the photos you already have." />
        <Studio onStartTrial={openTrial} />

        <Controls />
        <Setup />
        <Closing onStartTrial={openTrial} />
      </main>
      <Footer />
      <DemoScheduler isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} intent={modalIntent} />
    </div>
  );
}
